import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createFileRoute,
  redirect,
  Outlet,
  useNavigate,
  useRouterState,
  Link,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from '@vht/icons';
import { sessionQueryKey, fetchSession, appEnv } from '@/lib/auth';
import { currentBrand } from '@/lib/brand';
import { mayOpenPath } from '@/lib/access';
import { EnvBanner } from '@/components/shell/EnvBanner';
import { WorldBar } from '@/components/shell/WorldBar';
import { ModuleBar } from '@/components/shell/ModuleBar';
import { ShortcutsDialog } from '@/components/shell/ShortcutsDialog';
import { ReleaseNotesDialog } from '@/features/release-notes/ReleaseNotesDialog';
import { activeFor, visibleWorlds } from '@/components/shell/nav';
import { rememberModule, worldTarget } from '@/components/shell/lastModule';
import { isTyping } from '@/components/shell/menu';
import { ToastProvider, ToastRegion } from '@/components/ui/Toast';

export const Route = createFileRoute('/_shell')({
  beforeLoad: async ({ context, location }) => {
    // The backoffice is not on the names of Alfen: the Entra callback belongs to
    // our own name, and there is nothing here for a partner.
    if (currentBrand() === 'alfen') throw redirect({ to: '/portal' });
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { session };
  },
  component: ShellLayout,
});

function ShellLayout() {
  const { t } = useTranslation();
  const { session } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const allowed = mayOpenPath(pathname, session);

  const worlds = useMemo(() => visibleWorlds(session), [session]);
  const active = useMemo(() => activeFor(pathname, worlds), [pathname, worlds]);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (active) rememberModule(active.world.key, active.module.to);
  }, [active]);

  // ⌥1–9 to world n, [ and ] to the previous or next module, ? for the list.
  // Read `e.code`: on a Mac ⌥1 types "¡", so `e.key` never says "1".
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.altKey && !e.metaKey && !e.ctrlKey && /^Digit[1-9]$/.test(e.code)) {
        const world = worlds[Number(e.code.slice(5)) - 1];
        if (world) {
          e.preventDefault();
          void navigate({ to: worldTarget(world) });
        }
        return;
      }
      if (e.altKey || e.metaKey || e.ctrlKey || isTyping(e.target)) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      if ((e.key === '[' || e.key === ']') && active && active.world.modules.length > 1) {
        e.preventDefault();
        const modules = active.world.modules;
        const i = modules.indexOf(active.module);
        const step = e.key === ']' ? 1 : -1;
        void navigate({ to: modules[(i + step + modules.length) % modules.length]!.to });
      } else if (e.key === '?') {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [worlds, active, navigate]);

  const showEnvBanner = appEnv() !== 'production';

  return (
    <ToastProvider>
      <div className="app">
        <a
          className="app-skip"
          href="#main"
          onClick={(e) => {
            e.preventDefault();
            mainRef.current?.focus();
          }}
        >
          {t('shell.skipToContent')}
        </a>
        <div className="app-head">
          {showEnvBanner && <EnvBanner />}
          <WorldBar
            session={session}
            worlds={worlds}
            activeWorld={active?.world ?? null}
            onOpenShortcuts={() => setShortcutsOpen(true)}
            onOpenReleaseNotes={() => setReleaseNotesOpen(true)}
          />
          {active && <ModuleBar world={active.world} activeModule={active.module} />}
        </div>
        {/* Toasts hang 12 px under the head and over the content — and over a modal. */}
        <ToastRegion />
        <main className="app-main" id="main" ref={mainRef} tabIndex={-1}>
          <div className="app-page">{allowed ? <Outlet /> : <NoAccess />}</div>
        </main>
        {shortcutsOpen && <ShortcutsDialog onClose={() => setShortcutsOpen(false)} />}
        {releaseNotesOpen && <ReleaseNotesDialog onClose={() => setReleaseNotesOpen(false)} />}
      </div>
    </ToastProvider>
  );
}

/**
 * Wat je ziet als je een pad opent waar je rol niet bij mag.
 *
 * Bewust géén stille doorverwijzing: wie een bladwijzer volgt hoort te weten dat
 * het scherm bestaat maar niet voor hem is — anders lijkt het alsof de link stuk
 * is. De schil blijft staan, zodat verder klikken meteen kan.
 */
function NoAccess() {
  const { t } = useTranslation();
  return (
    <div className="empty" data-screen-label="Backoffice Geen toegang">
      <div className="empty__icon">
        <Icon name="lock" size={20} />
      </div>
      <h3>{t('access.deniedTitle')}</h3>
      <p>{t('access.deniedBody')}</p>
      <Link to="/dashboard" className="vht-btn vht-btn--secondary">
        {t('access.toDashboard')}
      </Link>
    </div>
  );
}
